/**
 * Where Dossiers is, from the URL hash, so the queue and each case are links
 * that can be shared:
 *
 * - `#cases` is the queue; `#cases?view=mine&channel=phone` the queue filtered;
 * - `#cases/<supportCaseId>` opens that case beside the queue, keeping the filters.
 */
import {
  SUPPORT_CASE_CHANNELS,
  SUPPORT_CASE_STATUSES,
  type ListSupportCasesRequest,
  type SupportCaseChannel,
  type SupportCaseStatus,
} from "@wallyo/contracts/console/cases";

/** The queue's tabs: Tous, Non attribués, Les miens, En retard. */
export const QUEUE_VIEWS = ["all", "unassigned", "mine", "late"] as const;
export type QueueView = (typeof QUEUE_VIEWS)[number];

export type QueueFilters = {
  view: QueueView;
  /** Unset: every case that isn't closed (the server's default). */
  status: SupportCaseStatus | null;
  channel: SupportCaseChannel | null;
  /** One customer's cases. */
  userId: string | null;
};

export const ALL_CASES: QueueFilters = { view: "all", status: null, channel: null, userId: null };

export type CasesRoute = { filters: QueueFilters; supportCaseId: string | null };

const oneOf = <T extends string>(values: readonly T[], value: string | null): T | null =>
  value !== null && (values as readonly string[]).includes(value) ? (value as T) : null;

export function parseCasesHash(hash: string): CasesRoute {
  const rest = hash.replace(/^#\/?cases/, "");
  const [path = "", search = ""] = rest.split("?", 2);
  const params = new URLSearchParams(search);
  const supportCaseId = decodeURIComponent(path.replace(/^\/+/, "").split("/")[0] ?? "").trim();
  return {
    filters: {
      view: oneOf(QUEUE_VIEWS, params.get("view")) ?? "all",
      status: oneOf(SUPPORT_CASE_STATUSES, params.get("status")),
      channel: oneOf(SUPPORT_CASE_CHANNELS, params.get("channel")),
      userId: params.get("userId")?.trim() || null,
    },
    supportCaseId: supportCaseId || null,
  };
}

/** The hash for the queue with these filters, and a case open beside it. */
export function casesHref(filters: QueueFilters, supportCaseId: string | null = null): string {
  const params = new URLSearchParams();
  if (filters.view !== "all") params.set("view", filters.view);
  if (filters.status) params.set("status", filters.status);
  if (filters.channel) params.set("channel", filters.channel);
  if (filters.userId) params.set("userId", filters.userId);
  const query = params.toString();
  const base = supportCaseId ? `#cases/${encodeURIComponent(supportCaseId)}` : "#cases";
  return query ? `${base}?${query}` : base;
}

/** What the queue asks the server for: the tab becomes `assigned` or `late`. */
export function queueRequest(filters: QueueFilters, cursor?: string | null): ListSupportCasesRequest {
  const request: ListSupportCasesRequest = {};
  if (filters.view === "mine" || filters.view === "unassigned") request.assigned = filters.view;
  if (filters.view === "late") request.late = true;
  if (filters.status) request.status = filters.status;
  if (filters.channel) request.channel = filters.channel;
  if (filters.userId) request.userId = filters.userId;
  if (cursor) request.cursor = cursor;
  return request;
}
